/**
 * DraftCommentsTable - 7-column table for Draft Review Mode
 *
 * Features:
 * - Column sorting (all columns)
 * - Client-side filtering by edit type, outcome status, section, geo
 * - Expandable rows for thread view
 * - Outcome dropdown that calls PATCH endpoint on change
 */
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  ExpandedState,
  FilterFn,
} from '@tanstack/react-table';
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  X,
  Filter,
  ChevronRight,
  MessageSquare,
  Trash2,
  Plus,
} from 'lucide-react';
import {
  DraftCommentSearchResult,
  DraftCommentMatch,
  OutcomeStatus,
} from '../types';

// Union type to accept both old and new result types
type DraftResultType = DraftCommentSearchResult | DraftCommentMatch;
import { authApi } from '../services/authApi';
import { logger } from '../lib/logger';

// =============================================================================
// Sub-Components
// =============================================================================

/**
 * EditTypeBadges - Renders colored badges for Comment, Deletion, Addition
 */
interface EditTypeBadgesProps {
  hasComment: boolean;
  hasDeletion: boolean;
  hasInsertion: boolean;
}

const EditTypeBadges: React.FC<EditTypeBadgesProps> = ({
  hasComment,
  hasDeletion,
  hasInsertion,
}) => {
  return (
    <div className="flex flex-wrap gap-1.5">
      {hasComment && (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-blue-50 to-sky-50 text-blue-700 border border-blue-100/60 shadow-soft-sm">
          <MessageSquare className="w-3 h-3" />
          Comment
        </span>
      )}
      {hasDeletion && (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border border-red-100/60 shadow-soft-sm">
          <Trash2 className="w-3 h-3" />
          Deletion
        </span>
      )}
      {hasInsertion && (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border border-emerald-100/60 shadow-soft-sm">
          <Plus className="w-3 h-3" />
          Addition
        </span>
      )}
    </div>
  );
};

/**
 * AffectedTextDiff - Renders context with strikethrough/highlight styling
 */
interface AffectedTextDiffProps {
  contextText: string | null;
  isDeleted: boolean;
  isInserted: boolean;
}

const AffectedTextDiff: React.FC<AffectedTextDiffProps> = ({
  contextText,
  isDeleted,
  isInserted,
}) => {
  if (!contextText) {
    return <span className="text-slate-400 text-sm italic">No context</span>;
  }

  // Truncate long text
  const maxLength = 150;
  const displayText = contextText.length > maxLength
    ? contextText.substring(0, maxLength) + '...'
    : contextText;

  if (isDeleted) {
    return (
      <span className="inline-block text-red-700 line-through bg-gradient-to-r from-red-50 to-red-100/50 px-2 py-0.5 rounded-md text-sm border border-red-100/50">
        {displayText}
      </span>
    );
  }

  if (isInserted) {
    return (
      <span className="inline-block text-emerald-700 bg-gradient-to-r from-emerald-50 to-green-100/50 px-2 py-0.5 rounded-md text-sm border border-emerald-100/50 underline decoration-emerald-400 decoration-2 underline-offset-2">
        {displayText}
      </span>
    );
  }

  return <span className="text-slate-600 text-sm leading-relaxed">{displayText}</span>;
};

/**
 * OutcomeDropdown - Dropdown that updates outcome via API
 */
interface OutcomeDropdownProps {
  commentId: number;
  currentStatus: OutcomeStatus | null;
  suggestedBy: 'llm' | 'user' | null;
  onStatusChange: (commentId: number, newStatus: OutcomeStatus) => void;
  isUpdating: boolean;
}

const OUTCOME_OPTIONS: { value: OutcomeStatus; label: string; bgClass: string; textClass: string; borderClass: string }[] = [
  { value: 'accepted', label: 'Accepted', bgClass: 'bg-gradient-to-r from-emerald-50 to-green-50', textClass: 'text-emerald-700', borderClass: 'border-emerald-200' },
  { value: 'countered_success', label: 'Countered (Success)', bgClass: 'bg-gradient-to-r from-blue-50 to-cyan-50', textClass: 'text-blue-700', borderClass: 'border-blue-200' },
  { value: 'countered_failed', label: 'Countered (Failed)', bgClass: 'bg-gradient-to-r from-orange-50 to-amber-50', textClass: 'text-orange-700', borderClass: 'border-orange-200' },
  { value: 'ongoing', label: 'Ongoing', bgClass: 'bg-gradient-to-r from-amber-50 to-yellow-50', textClass: 'text-amber-700', borderClass: 'border-amber-200' },
];

const OutcomeDropdown: React.FC<OutcomeDropdownProps> = ({
  commentId,
  currentStatus,
  suggestedBy,
  onStatusChange,
  isUpdating,
}) => {
  const currentOption = OUTCOME_OPTIONS.find(o => o.value === currentStatus);

  return (
    <div className="relative group">
      <select
        value={currentStatus || ''}
        onChange={(e) => {
          if (e.target.value) {
            onStatusChange(commentId, e.target.value as OutcomeStatus);
          }
        }}
        disabled={isUpdating}
        className={`
          px-3 py-1.5 text-xs font-medium rounded-lg border cursor-pointer
          transition-all duration-200 shadow-soft-sm
          ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-soft'}
          ${currentOption
            ? `${currentOption.bgClass} ${currentOption.textClass} ${currentOption.borderClass}`
            : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'}
          focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-1
        `}
      >
        <option value="">Select outcome</option>
        {OUTCOME_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {suggestedBy === 'llm' && currentStatus && (
        <span
          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full border-2 border-white shadow-soft-sm flex items-center justify-center"
          title="LLM suggested"
        >
          <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" />
          </svg>
        </span>
      )}
    </div>
  );
};

// =============================================================================
// Filter Functions
// =============================================================================

const editTypeFilter: FilterFn<DraftResultType> = (
  row,
  _columnId,
  filterValue: ('comment' | 'deletion' | 'insertion')[]
) => {
  if (!filterValue || filterValue.length === 0) return true;

  const hasMatch = filterValue.some(type => {
    if (type === 'comment') return row.original.has_comment;
    if (type === 'deletion') return row.original.has_deletion;
    if (type === 'insertion') return row.original.has_insertion;
    return false;
  });

  return hasMatch;
};

const outcomeFilter: FilterFn<DraftResultType> = (
  row,
  _columnId,
  filterValue: OutcomeStatus[]
) => {
  if (!filterValue || filterValue.length === 0) return true;
  return filterValue.includes(row.original.outcome_status!);
};

const geoFilter: FilterFn<DraftResultType> = (
  row,
  _columnId,
  filterValue: string[]
) => {
  if (!filterValue || filterValue.length === 0) return true;
  const region = row.original.region || 'Unknown';
  return filterValue.includes(region);
};

// =============================================================================
// Column Helper
// =============================================================================

const columnHelper = createColumnHelper<DraftResultType>();

// =============================================================================
// Main Component
// =============================================================================

interface DraftCommentsTableProps {
  results: DraftResultType[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

const DraftCommentsTable: React.FC<DraftCommentsTableProps> = ({
  results,
  isLoading = false,
  onRefresh,
}) => {
  const navigate = useNavigate();

  // View toggle: comments vs label negotiations (sections)
  const [draftView, setDraftView] = useState<'comments' | 'sections'>('comments');

  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Filter UI state
  const [showFilters, setShowFilters] = useState(false);
  const [selectedEditTypes, setSelectedEditTypes] = useState<('comment' | 'deletion' | 'insertion')[]>([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState<OutcomeStatus[]>([]);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [selectedGeos, setSelectedGeos] = useState<string[]>([]);

  // Outcome update state
  const [updatingCommentId, setUpdatingCommentId] = useState<number | null>(null);

  // Local results state for optimistic updates
  const [localResults, setLocalResults] = useState(results);

  // Sync with props
  useEffect(() => {
    setLocalResults(results);
  }, [results]);

  // Count rows by result_kind (missing/undefined treated as 'comment')
  const nComments = useMemo(
    () => localResults.filter(r => (r as DraftCommentMatch).result_kind !== 'section').length,
    [localResults]
  );
  const nSections = useMemo(
    () => localResults.filter(r => (r as DraftCommentMatch).result_kind === 'section').length,
    [localResults]
  );

  // Filter rows according to active view tab
  const viewFilteredResults = useMemo(() => {
    if (draftView === 'sections') {
      return localResults.filter(r => (r as DraftCommentMatch).result_kind === 'section');
    }
    // 'comments' view: rows where result_kind is not 'section'
    return localResults.filter(r => (r as DraftCommentMatch).result_kind !== 'section');
  }, [localResults, draftView]);

  // Handle outcome status change
  const handleOutcomeChange = useCallback(async (commentId: number, newStatus: OutcomeStatus) => {
    setUpdatingCommentId(commentId);

    // Optimistic update
    setLocalResults(prev =>
      prev.map(r =>
        r.comment_id === commentId
          ? { ...r, outcome_status: newStatus, outcome_suggested_by: 'user' as const }
          : r
      )
    );

    try {
      await authApi.updateCommentOutcome(commentId, newStatus);
      logger.info('Outcome updated', { commentId, newStatus });
    } catch (error) {
      logger.error('Failed to update outcome', { error, commentId, newStatus });
      // Revert on error
      setLocalResults(results);
    } finally {
      setUpdatingCommentId(null);
    }
  }, [results]);

  // Navigate to drug detail with comment highlighted
  const handleRowClick = useCallback((row: DraftResultType) => {
    if (row.drug_id && row.label_version_id) {
      navigate(`/drug/${row.drug_id}?version=${row.label_version_id}&comment=${row.comment_id}`);
    }
  }, [navigate]);

  // Extract unique sections from results for filter options
  const availableSections = useMemo(() => {
    const sectionSet = new Map<string, { number: string | null; title: string | null }>();
    let hasUnknown = false;

    localResults.forEach(r => {
      // Handle both undefined and null (DraftCommentSearchResult has optional fields)
      const sectionNumber = r.section_number ?? null;
      const sectionTitle = r.section_title ?? null;

      if (!sectionNumber && !sectionTitle) {
        hasUnknown = true;
      } else {
        // Create a unique key for deduplication
        const key = sectionNumber
          ? sectionTitle
            ? `${sectionNumber} - ${sectionTitle}`
            : sectionNumber
          : sectionTitle || '';
        if (key && !sectionSet.has(key)) {
          sectionSet.set(key, { number: sectionNumber, title: sectionTitle });
        }
      }
    });

    // Convert to array and sort by section number
    const sections = Array.from(sectionSet.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => {
        // Sort by section number numerically if possible
        const numA = a.number ? parseFloat(a.number) : Infinity;
        const numB = b.number ? parseFloat(b.number) : Infinity;
        if (numA !== numB) return numA - numB;
        return (a.title || '').localeCompare(b.title || '');
      });

    return { sections, hasUnknown };
  }, [localResults]);

  // Extract unique geos from results for filter options
  const availableGeos = useMemo(() => {
    const geoSet = new Set<string>();
    localResults.forEach(r => {
      const region = r.region;
      if (region) geoSet.add(region);
    });
    return Array.from(geoSet).sort();
  }, [localResults]);

  // Apply filters to table
  useEffect(() => {
    const newFilters: ColumnFiltersState = [];

    if (selectedEditTypes.length > 0) {
      newFilters.push({
        id: 'edit_type',
        value: selectedEditTypes,
      });
    }

    if (selectedOutcomes.length > 0) {
      newFilters.push({
        id: 'outcome_status',
        value: selectedOutcomes,
      });
    }

    if (selectedSections.length > 0) {
      newFilters.push({
        id: 'section',
        value: selectedSections,
      });
    }

    if (selectedGeos.length > 0) {
      newFilters.push({
        id: 'geo',
        value: selectedGeos,
      });
    }

    setColumnFilters(newFilters);
  }, [selectedEditTypes, selectedOutcomes, selectedSections, selectedGeos]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSelectedEditTypes([]);
    setSelectedOutcomes([]);
    setSelectedSections([]);
    setSelectedGeos([]);
  }, []);

  // Toggle edit type selection
  const toggleEditType = useCallback((type: 'comment' | 'deletion' | 'insertion') => {
    setSelectedEditTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  }, []);

  // Toggle outcome selection
  const toggleOutcome = useCallback((outcome: OutcomeStatus) => {
    setSelectedOutcomes(prev =>
      prev.includes(outcome) ? prev.filter(o => o !== outcome) : [...prev, outcome]
    );
  }, []);

  // Toggle section selection
  const toggleSection = useCallback((section: string) => {
    setSelectedSections(prev =>
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  }, []);

  // Toggle geo selection
  const toggleGeo = useCallback((geo: string) => {
    setSelectedGeos(prev =>
      prev.includes(geo) ? prev.filter(g => g !== geo) : [...prev, geo]
    );
  }, []);

  // ---- Column definitions ----

  // Shared: expand chevron
  const expandColumn = columnHelper.display({
    id: 'expand',
    header: () => null,
    cell: ({ row }) => {
      const canExpand = row.original.thread_count > 0 || row.original.is_reply;
      if (!canExpand) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
          className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors group/expand"
        >
          <ChevronRight
            className={`w-4 h-4 text-slate-400 group-hover/expand:text-indigo-500 transition-all duration-200 ${
              row.getIsExpanded() ? 'rotate-90 text-indigo-500' : ''
            }`}
          />
        </button>
      );
    },
    size: 40,
  });

  // Shared: Drug Name
  const drugNameColumn = columnHelper.accessor('brand_name', {
    header: 'Drug Name',
    cell: ({ getValue, row }) => (
      <div className="flex flex-col gap-0.5">
        <span className="font-semibold text-indigo-600 group-hover:text-indigo-700 transition-colors">
          {getValue() || 'Unknown Drug'}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          Version {row.original.version_number}
        </span>
      </div>
    ),
    size: 160,
  });

  // Shared: Section
  const sectionColumn = columnHelper.display({
    id: 'section',
    header: 'Section',
    cell: ({ row }) => {
      const num = row.original.section_number;
      const title = row.original.section_title;
      if (!num && !title) {
        return <span className="text-slate-400 text-sm italic">-</span>;
      }
      return (
        <div className="flex flex-col gap-0.5">
          {num && <span className="text-xs font-semibold text-indigo-600">{num}</span>}
          {title && title !== num && (
            <span className="text-xs text-slate-600 line-clamp-2">{title}</span>
          )}
        </div>
      );
    },
    filterFn: (row, _columnId, filterValue: string[]) => {
      if (!filterValue || filterValue.length === 0) return true;
      const num = row.original.section_number ?? null;
      const title = row.original.section_title ?? null;
      if (filterValue.includes('__unknown__') && !num && !title) return true;
      const key = num
        ? title && title !== num ? `${num} - ${title}` : num
        : title || '';
      return filterValue.includes(key);
    },
    size: 140,
  });

  // Comments view columns (full 7-column set)
  const commentsColumns = useMemo(
    () => [
      expandColumn,
      drugNameColumn,

      // Type of Edit
      columnHelper.display({
        id: 'edit_type',
        header: 'Type of Edit',
        cell: ({ row }) => (
          <EditTypeBadges
            hasComment={row.original.has_comment}
            hasDeletion={row.original.has_deletion}
            hasInsertion={row.original.has_insertion}
          />
        ),
        filterFn: editTypeFilter,
        size: 180,
      }),

      // The Comment
      columnHelper.accessor('comment_text', {
        header: 'Comment',
        cell: ({ getValue, row }) => {
          const text = getValue();
          const isReply = row.original.is_reply;
          if (!text) {
            return <span className="text-slate-400 italic text-sm">No comment text</span>;
          }
          const maxLength = 100;
          const displayText = text.length > maxLength
            ? text.substring(0, maxLength) + '...'
            : text;

          return (
            <div className="flex items-start gap-2">
              {isReply && (
                <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border border-amber-200/60" title="This is a reply to another comment">
                  <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Reply
                </span>
              )}
              <span className="text-slate-600 text-sm leading-relaxed">{displayText}</span>
              {row.original.thread_count > 0 && (
                <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-600 border border-indigo-100/60">
                  <MessageSquare className="w-2.5 h-2.5" />
                  {row.original.thread_count}
                </span>
              )}
            </div>
          );
        },
        size: 400,
      }),

      // Date
      columnHelper.accessor('action_date', {
        header: 'Date',
        cell: ({ getValue }) => {
          const dateStr = getValue();
          if (!dateStr) return <span className="text-slate-400 text-sm">-</span>;
          try {
            const date = new Date(dateStr);
            return (
              <span className="text-slate-600 text-sm">
                {date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            );
          } catch {
            return <span className="text-slate-400 text-sm">-</span>;
          }
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.action_date;
          const b = rowB.original.action_date;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return new Date(a).getTime() - new Date(b).getTime();
        },
        size: 110,
      }),

      // Affected Text
      columnHelper.display({
        id: 'affected_text',
        header: 'Affected Text',
        cell: ({ row }) => (
          <AffectedTextDiff
            contextText={row.original.context_text}
            isDeleted={row.original.context_is_deleted}
            isInserted={row.original.context_is_inserted}
          />
        ),
        size: 220,
      }),

      // Outcome
      columnHelper.accessor('outcome_status', {
        header: 'Outcome',
        cell: ({ row }) => (
          <OutcomeDropdown
            commentId={row.original.comment_id}
            currentStatus={row.original.outcome_status}
            suggestedBy={row.original.outcome_suggested_by}
            onStatusChange={handleOutcomeChange}
            isUpdating={updatingCommentId === row.original.comment_id}
          />
        ),
        filterFn: outcomeFilter,
        size: 160,
      }),

      sectionColumn,

      // Geo
      columnHelper.display({
        id: 'geo',
        header: 'Geo',
        cell: ({ row }) => {
          const region = row.original.region;
          if (!region) {
            return <span className="text-slate-400 text-sm">-</span>;
          }
          const geoConfig: Record<string, { bg: string; text: string }> = {
            'US': { bg: 'bg-gradient-to-r from-blue-50 to-sky-50', text: 'text-blue-700' },
            'EU': { bg: 'bg-gradient-to-r from-amber-50 to-yellow-50', text: 'text-amber-700' },
            'UK': { bg: 'bg-gradient-to-r from-red-50 to-rose-50', text: 'text-red-700' },
            'CA': { bg: 'bg-gradient-to-r from-red-50 to-orange-50', text: 'text-red-600' },
            'AU': { bg: 'bg-gradient-to-r from-green-50 to-emerald-50', text: 'text-green-700' },
            'JP': { bg: 'bg-gradient-to-r from-rose-50 to-pink-50', text: 'text-rose-700' },
          };
          const config = geoConfig[region] || { bg: 'bg-slate-50', text: 'text-slate-700' };
          return (
            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text} border border-current/10 shadow-soft-sm`}>
              {region}
            </span>
          );
        },
        filterFn: geoFilter,
        size: 80,
      }),
    ],
    [handleOutcomeChange, updatingCommentId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Label Negotiations (sections) view columns: Drug Name, Section, Content
  const sectionsColumns = useMemo(
    () => [
      drugNameColumn,
      sectionColumn,

      // Content — section/Label-Negotiations hits carry their text in
      // section_excerpt (comment_text is null for them; _convert_sections_to_comment_format
      // sets comment_text=None and puts the chunk text in section_excerpt/context_text).
      columnHelper.display({
        id: 'content',
        header: 'Content',
        cell: ({ row }) => {
          const text = row.original.section_excerpt
            || row.original.context_text
            || row.original.comment_text
            || '';
          if (!text) {
            return <span className="text-slate-400 italic text-sm">No content</span>;
          }
          const maxLength = 300;
          const displayText = text.length > maxLength
            ? text.substring(0, maxLength) + '...'
            : text;
          return <span className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{displayText}</span>;
        },
        size: 600,
      }),
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Active column set driven by draftView
  const columns = useMemo(
    () => draftView === 'sections' ? sectionsColumns : commentsColumns,
    [draftView, commentsColumns, sectionsColumns]
  );

  // Create table instance
  const table = useReactTable({
    data: viewFilteredResults,
    columns,
    state: {
      sorting,
      columnFilters,
      expanded,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => row.original.thread_count > 0 || Boolean(row.original.is_reply),
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const hasActiveFilters = selectedEditTypes.length > 0 || selectedOutcomes.length > 0 || selectedSections.length > 0 || selectedGeos.length > 0;
  const activeFilterCount = [
    selectedEditTypes.length > 0,
    selectedOutcomes.length > 0,
    selectedSections.length > 0,
    selectedGeos.length > 0,
  ].filter(Boolean).length;

  return (
    <div className="w-full">
      {/* View Toggle: Comments | Label Negotiations */}
      <div className="mb-4 flex items-center">
        <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1 shadow-soft-sm">
          <button
            onClick={() => setDraftView('comments')}
            className={`
              inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200
              ${draftView === 'comments'
                ? 'bg-white text-indigo-700 shadow-soft border border-indigo-100/60'
                : 'text-slate-500 hover:text-slate-700'
              }
            `}
          >
            <MessageSquare className="w-4 h-4" />
            Comments
            <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-bold rounded-full ${draftView === 'comments' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`}>
              {nComments}
            </span>
          </button>
          <button
            onClick={() => setDraftView('sections')}
            className={`
              inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200
              ${draftView === 'sections'
                ? 'bg-white text-indigo-700 shadow-soft border border-indigo-100/60'
                : 'text-slate-500 hover:text-slate-700'
              }
            `}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Label Negotiations
            <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-bold rounded-full ${draftView === 'sections' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`}>
              {nSections}
            </span>
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`
              inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 shadow-soft-sm
              ${showFilters || hasActiveFilters
                ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border border-indigo-200/80 shadow-soft'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200 hover:border-slate-300'
              }
            `}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full shadow-soft-sm">
                {activeFilterCount}
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Clear all
            </button>
          )}

          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="font-medium text-slate-700">{filteredRowCount}</span>
            <span>of</span>
            <span>{viewFilteredResults.length}</span>
            <span>{draftView === 'sections' ? 'sections' : 'comments'}</span>
          </div>

          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 transition-all duration-200 shadow-soft-sm hover:shadow-soft"
            >
              <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          )}
        </div>

        {/* Expandable Filter Panel */}
        {showFilters && (
          <div className="p-5 bg-gradient-to-br from-slate-50 to-indigo-50/30 rounded-xl border border-slate-200/80 shadow-inner-soft animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Edit Type Filter */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Type of Edit</label>
                <div className="flex flex-wrap gap-2">
                  {(['comment', 'deletion', 'insertion'] as const).map((type) => {
                    const config = {
                      comment: { label: 'Comment', icon: MessageSquare, selectedBg: 'from-blue-500 to-blue-600', hoverBorder: 'hover:border-blue-300' },
                      deletion: { label: 'Deletion', icon: Trash2, selectedBg: 'from-red-500 to-red-600', hoverBorder: 'hover:border-red-300' },
                      insertion: { label: 'Addition', icon: Plus, selectedBg: 'from-emerald-500 to-emerald-600', hoverBorder: 'hover:border-emerald-300' },
                    }[type];
                    const Icon = config.icon;
                    const isSelected = selectedEditTypes.includes(type);

                    return (
                      <button
                        key={type}
                        onClick={() => toggleEditType(type)}
                        className={`
                          inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200
                          ${isSelected
                            ? `bg-gradient-to-r ${config.selectedBg} text-white shadow-soft`
                            : `bg-white border border-slate-200 text-slate-600 ${config.hoverBorder} hover:shadow-soft-sm`
                          }
                        `}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {config.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Outcome Filter */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Outcome Status</label>
                <div className="flex flex-wrap gap-2">
                  {OUTCOME_OPTIONS.map((option) => {
                    const isSelected = selectedOutcomes.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        onClick={() => toggleOutcome(option.value)}
                        className={`
                          px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200
                          ${isSelected
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-soft'
                            : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-300 hover:shadow-soft-sm'
                          }
                        `}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Section Filter */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Section</label>
                <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto pr-2 scrollbar-thin">
                  {availableSections.hasUnknown && (
                    <button
                      onClick={() => toggleSection('__unknown__')}
                      className={`
                        px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200
                        ${selectedSections.includes('__unknown__')
                          ? 'bg-gradient-to-r from-slate-500 to-slate-600 text-white shadow-soft'
                          : 'bg-white border border-slate-200 text-slate-500 hover:border-slate-400 italic hover:shadow-soft-sm'
                        }
                      `}
                    >
                      Unknown
                    </button>
                  )}
                  {availableSections.sections.map((section) => {
                    const isSelected = selectedSections.includes(section.key);
                    // Truncate long labels
                    const displayLabel = section.key.length > 20
                      ? section.key.substring(0, 20) + '...'
                      : section.key;
                    return (
                      <button
                        key={section.key}
                        onClick={() => toggleSection(section.key)}
                        title={section.key}
                        className={`
                          px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200
                          ${isSelected
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-soft'
                            : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-300 hover:shadow-soft-sm'
                          }
                        `}
                      >
                        {displayLabel}
                      </button>
                    );
                  })}
                  {availableSections.sections.length === 0 && !availableSections.hasUnknown && (
                    <span className="text-xs text-slate-400 italic py-1.5">No sections found</span>
                  )}
                </div>
              </div>

              {/* Geo Filter */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Geo</label>
                <div className="flex flex-wrap gap-2">
                  {availableGeos.length > 0 ? availableGeos.map((geo) => {
                    const isSelected = selectedGeos.includes(geo);
                    return (
                      <button
                        key={geo}
                        onClick={() => toggleGeo(geo)}
                        className={`
                          px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200
                          ${isSelected
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-soft'
                            : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-300 hover:shadow-soft-sm'
                          }
                        `}
                      >
                        {geo}
                      </button>
                    );
                  }) : (
                    <span className="text-xs text-slate-400 italic py-1.5">No regions found</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border border-slate-200/80 rounded-xl overflow-hidden shadow-soft bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100/80 border-b border-slate-200/80">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider"
                      style={{ width: header.getSize() }}
                    >
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <button
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex items-center gap-1 hover:text-slate-700 transition-colors group"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <ChevronUp className="w-4 h-4 text-indigo-500" />,
                            desc: <ChevronDown className="w-4 h-4 text-indigo-500" />,
                          }[header.column.getIsSorted() as string] ?? (
                            <ChevronsUpDown className="w-4 h-4 text-slate-300 group-hover:text-slate-400 transition-colors" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-100/80">
              {table.getRowModel().rows.map((row, rowIndex) => (
                <React.Fragment key={row.id}>
                  <tr
                    onClick={() => handleRowClick(row.original)}
                    className="cursor-pointer transition-all duration-150 hover:bg-gradient-to-r hover:from-indigo-50/40 hover:to-purple-50/20 hover:shadow-[inset_3px_0_0_0_theme(colors.indigo.400)] group"
                    style={{ animationDelay: `${rowIndex * 20}ms` }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-4 py-3.5"
                        onClick={(e) => {
                          // Prevent row click when clicking expand or outcome dropdown
                          if (cell.column.id === 'expand' || cell.column.id === 'outcome_status') {
                            e.stopPropagation();
                          }
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {/* Expanded row for thread context */}
                  {row.getIsExpanded() && (
                    <tr className="animate-fade-in">
                      <td colSpan={columns.length} className="bg-gradient-to-r from-slate-50 to-indigo-50/30 px-8 py-5 border-l-2 border-indigo-200">
                        <div className="text-sm text-slate-600">
                          {/* Dynamic header based on context */}
                          <p className="font-medium text-slate-700 mb-3">
                            {row.original.is_reply
                              ? `Thread Context (${row.original.thread_count || 0} other comment${(row.original.thread_count || 0) !== 1 ? 's' : ''})`
                              : `Replies (${row.original.thread_count || 0})`}
                          </p>

                          {/* Display thread_replies if available */}
                          {'thread_replies' in row.original && row.original.thread_replies && row.original.thread_replies.length > 0 ? (
                            <div className="space-y-3">
                              {row.original.thread_replies.map((reply: { comment_id: number; comment_text: string; author: string | null }, idx: number) => {
                                // For reply rows, first item is the parent comment
                                const isParent = row.original.is_reply && idx === 0;
                                return (
                                  <div
                                    key={reply.comment_id}
                                    className={`flex gap-3 p-3 rounded-lg ${
                                      isParent
                                        ? 'bg-indigo-50 border border-indigo-200'
                                        : 'bg-white border border-slate-200'
                                    }`}
                                  >
                                    <div className="flex-shrink-0">
                                      {isParent ? (
                                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded bg-indigo-100 text-indigo-700">
                                          Parent
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-600">
                                          ↳ Reply
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium text-slate-700">
                                          {reply.author || 'Unknown Author'}
                                        </span>
                                      </div>
                                      <p className="text-slate-600 whitespace-pre-wrap">
                                        {reply.comment_text}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-slate-500 italic">
                              No thread data available. Click row to view full context on drug detail page.
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-xs flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 border-4 border-indigo-100 rounded-full" />
                <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-indigo-500 rounded-full animate-spin" />
              </div>
              <span className="text-sm text-slate-500 font-medium">Loading comments...</span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {table.getRowModel().rows.length === 0 && !isLoading && (
          <div className="py-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200">
              <MessageSquare className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-base font-medium text-slate-600">
              {draftView === 'sections' ? 'No label negotiations found' : 'No draft comments found'}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Upload a DOCX file to get started'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DraftCommentsTable;
